/**
 * Urgent task alert: sunet (Web Audio API — fără fișier extern) + vibrație (Vibration API)
 * + notificare OS (Notification API) dacă utilizatorul a acordat permisiunea.
 *
 * Utilizare:
 *   import { triggerUrgentAlert, hasBeenAlerted, markAlerted, requestNotifPermissionOnce } from "../lib/urgentAlert";
 *   const newOnes = tasks.filter((t) => !hasBeenAlerted(t.id));
 *   if (newOnes.length > 0) {
 *     triggerUrgentAlert({ title: t.title, body: t.description });
 *     newOnes.forEach((t) => markAlerted(t.id));
 *   }
 *
 * Stochează ID-urile alertate în localStorage cu TTL implicit 7 zile pentru a
 * evita re-declanșarea la fiecare refresh/poll.
 */

const STORAGE_KEY = "convic_urgent_alerts_v1";
const PERM_ASKED_KEY = "convic_notif_perm_asked_v1";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 zile

function _readMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
    return {};
  } catch {
    return {};
  }
}

function _writeMap(m) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(m));
  } catch { /* quota / private mode */ }
}

function _gc(m) {
  const now = Date.now();
  let changed = false;
  for (const k of Object.keys(m)) {
    if (typeof m[k] !== "number" || now - m[k] > TTL_MS) {
      delete m[k];
      changed = true;
    }
  }
  if (changed) _writeMap(m);
  return m;
}

export function hasBeenAlerted(taskId) {
  if (!taskId) return true;
  const m = _gc(_readMap());
  return !!m[taskId];
}

export function markAlerted(taskId) {
  if (!taskId) return;
  const m = _readMap();
  m[taskId] = Date.now();
  _writeMap(m);
}

/**
 * Redă un beep de alarmă (2× tonuri ascendente) prin Web Audio API.
 * Robust: nu aruncă dacă browser-ul blochează autoplay sau nu suportă API.
 */
function _playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    // 2 perechi de beep-uri scurte (pattern de alarmă)
    const beep = (offset, freq, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.3, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + offset);
      osc.stop(now + offset + dur + 0.02);
    };
    beep(0,    880, 0.18);
    beep(0.22, 1320, 0.18);
    beep(0.55, 880, 0.18);
    beep(0.77, 1320, 0.22);
    // Cleanup context după 2s
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 2000);
    return true;
  } catch {
    return false;
  }
}

/**
 * Vibrează dispozitivul (pattern lung-pauză-lung). Funcționează doar pe mobile
 * cu navigator.vibrate (Android Chrome / Firefox). iOS nu suportă oficial.
 */
function _vibrate() {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate([400, 150, 400, 150, 600]);
      return true;
    }
  } catch { /* noop */ }
  return false;
}

/**
 * Cere o singură dată permisiunea de notificări OS (Notification API).
 * Rulează idempotent — folosește localStorage ca să nu deranjeze utilizatorul repetat.
 * Returnează "granted" | "denied" | "default" | "unsupported".
 */
export async function requestNotifPermissionOnce() {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";
    // Pe Chrome modern Notification.requestPermission e blocată în afara unui user-gesture,
    // dar AssignedTasksBanner load() este apelat ca răspuns la sesiunea autentificată
    // (utilizatorul a făcut click pe Login). Încercăm — dacă eșuează, marcăm "asked".
    if (localStorage.getItem(PERM_ASKED_KEY) === "1") return Notification.permission;
    localStorage.setItem(PERM_ASKED_KEY, "1");
    const res = await Notification.requestPermission();
    return res;
  } catch {
    return "unsupported";
  }
}

/**
 * Afișează o notificare OS dacă permisiunea e acordată.
 * Folosit pentru a alerta utilizatorul chiar dacă tab-ul ConVic e pe fundal.
 */
function _showOsNotification(title, body) {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return false;
    if (Notification.permission !== "granted") return false;
    const n = new Notification(title, {
      body: body || "",
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      tag: "convic-urgent-task",
      requireInteraction: true,
      vibrate: [400, 150, 400, 150, 600],
    });
    n.onclick = () => { try { window.focus(); n.close(); } catch { /* noop */ } };
    return true;
  } catch {
    return false;
  }
}

/**
 * Declanșează alerta urgent: sunet + vibrație + (opțional) notificare OS.
 * @param {Object} opts - { title?: string, body?: string }
 * Returnează un obiect cu flag-uri ce confirmă ce a funcționat.
 */
export function triggerUrgentAlert(opts = {}) {
  const sound = _playBeep();
  const vibration = _vibrate();
  const os = (opts.title || opts.body)
    ? _showOsNotification(opts.title || "⚡ Task URGENT", opts.body || "")
    : false;
  return { sound, vibration, os };
}
